import { PROJECT_BASE } from '../src_frame/fileMap.js'

// The frame worker's importData (src_frame/importData.js) reads these as
// binary, so only these cross as ArrayBuffer.
const BINARY_EXT = new Set(['stl', 'ttf', 'otf'])

/** @param {string} path */
export const isBinaryPath = (path) => BINARY_EXT.has(path.slice(path.lastIndexOf('.') + 1).toLowerCase())

/**
 * The project file an editor path names, or null for a URL (an example or a
 * remote script).
 * @param {string} path
 */
export const projectPathOf = (path) => (/^[a-z][a-z\d+.-]*:/i.test(path) ? null : path.replace(/^\//, ''))

/**
 * An editor run path that belongs to a project (record it) versus a URL —
 * an example, remote or data: script — that must never be stored into one.
 * @param {string} path
 */
export const isRecordablePath = (path) => projectPathOf(path) !== null

/**
 * What the frame isolates one load from the next by: one project id for
 * project urls, the url itself for examples, remote and data: scripts.
 * @param {{url?: string, projectId?: string}} load
 */
export const loadSource = ({ url, projectId }) =>
  (typeof url === 'string' && url.startsWith(PROJECT_BASE) ? `project:${projectId}` : url)

/**
 * The project to open on load: the most recently updated one, skipping the
 * disk row (it sorts first and opens from its own #/models hash).
 * @param {{id:string, mode:string}[]} projects
 * @returns {string | null}
 */
export const pickBootProject = (projects) => projects.find((p) => p.mode !== 'disk')?.id ?? null

/**
 * Whether boot reopens the last-used stored project. A URL-driven load names
 * its own content and must win: a `#/...` hash route loaded its script through
 * `remote`, and a launcher `#/models` disk entry opens by its own hash. Only a
 * plain load with a stored project to restore goes on to `pickBootProject`.
 * @param {{hasRemoteScript?: boolean, diskEntry?: string | null, projects?: unknown[]}} state
 */
export const shouldRestoreBootProject = ({ hasRemoteScript = false, diskEntry = null, projects = [] } = {}) =>
  !hasRemoteScript && diskEntry === null && projects.length > 0

/**
 * The files a seeded default project starts with. `main.js` is the two-cars
 * example, which requires its sibling `./02-hierarchical-car.example.js`; the
 * sibling travels with it so the stored project resolves the require and
 * builds as a project, not only as the example URL whose directory has the
 * file.
 * @param {{main: string, hierarchicalCar: string}} sources
 */
export const defaultProjectFiles = ({ main, hierarchicalCar }) => ({
  'main.js': main,
  '02-hierarchical-car.example.js': hierarchicalCar,
})

/**
 * The frame's worker is on another origin, so the file service worker cannot
 * serve it: a service worker only sees fetches from clients it controls. The
 * project travels in the message instead.
 *
 * addToCache() puts entries under `new Request(path)` where path is a
 * leading-slash, project-relative path (e.g. `/index.js`); against the
 * document origin that resolves to no `sw.base`/swfs prefix at all, just the
 * bare pathname. Keys here drop only that leading slash, matching the
 * leading-slash-free lookup in src_frame/fileMap.js's createReadFile.
 * @param {{base:string,cache:Cache}|undefined} sw
 * @returns {Promise<Record<string,string|ArrayBuffer>>}
 */
/**
 * Make the cache hold exactly this project. Without the clear the frame gets
 * the union of every project opened this session, since collectProjectFiles
 * sends whatever is in the cache.
 * @param {{clearProjectCache:()=>Promise<void>,addToCacheWrapper:(path:string,content:unknown)=>Promise<void>}} fileSystem
 * @param {Record<string,unknown>} files
 */
export const replaceProjectFiles = async (fileSystem, files) => {
  await fileSystem.clearProjectCache()
  for (const [path, content] of Object.entries(files)) {
    await fileSystem.addToCacheWrapper(path, content)
  }
}

/**
 * The editor's file list: each file named by its leading-slash path.
 * @param {Record<string,unknown>} files
 */
export const toEditorFiles = (files) =>
  Object.entries(files).map(([path, content]) =>
    Object.assign(new File([/** @type {BlobPart} */ (content)], path.split('/').pop()), { fullPath: `/${path}` }),
  )

/**
 * Open a stored project: the cache, the editor and the caller's open-project
 * state all hold it, then its entry builds.
 * @param {{
 *   manager: { readForSwitch: (id:string) => Promise<{ project: { entry: string, name: string }, files: Record<string,unknown> }> },
 *   fileSystem: Parameters<typeof replaceProjectFiles>[0],
 *   editor: { setFiles: (files:File[], options?:{keepBuffers?:boolean}) => void, setSource: (source:unknown, path:string) => void, confirmDiscard?: () => Promise<boolean> },
 *   clearTempCache: () => void,
 *   build: (entry:string) => Promise<unknown>,
 *   onOpen: (id:string, entry:string, name:string) => void,
 *   onError: (err:unknown) => void,
 * }} deps
 */
export const createProjectSwitch = ({ manager, fileSystem, editor, clearTempCache, build, onOpen, onError }) => async (id) => {
  if (editor.confirmDiscard && !(await editor.confirmDiscard())) return
  const { project, files } = await manager.readForSwitch(id)
  onOpen(id, project.entry, project.name)
  clearTempCache()
  await replaceProjectFiles(fileSystem, files)
  editor.setFiles(toEditorFiles(files))
  editor.setSource(files[project.entry] ?? '', project.entry)
  build(project.entry).catch(onError)
}

export const collectProjectFiles = async (sw) => {
  if (!sw?.cache) return {}
  const files = {}
  for (const request of await sw.cache.keys()) {
    const path = new URL(request.url).pathname.replace(/^\//, '')
    const response = await sw.cache.match(request)
    if (!response) continue
    files[path] = isBinaryPath(path) ? await response.arrayBuffer() : await response.text()
  }
  return files
}
